/**
 * @file DomainCheckMarks.tsx
 * @description Значки проверок адреса: DNS, прокси, HTTPS (phase19.28 этап А;
 * uiux-platform-shops §2, §5, §9 «не только цвет»).
 *
 * Каждый значок — ✓ / ✗ / «—» с текстом в подсказке и `aria-label`
 * («DNS указывает на сервер»), у HTTPS — срок сертификата в днях. Одна
 * отметка (`DomainCheckMark`) — для ячейки таблицы, набор (`DomainCheckMarks`)
 * — для строки карточки.
 */

import { Check, Minus, X } from "lucide-react";

import type { ShopDomainCheck } from "../../../lib/api-platform-shops";
import { CERT_WARN_DAYS, certDaysLeft } from "../../../lib/platform/shop-domain-status";
import { cn } from "@/lib/utils";

export type CheckPart = "dns" | "proxy" | "cert";

interface PartMeta {
  title: string;
  ok: string;
  /** Текст неудачи; `expectedIps` — адреса сервера платформы (edge-config). */
  fail: (check: ShopDomainCheck, expectedIps: readonly string[]) => string;
}

/** Реестр проверок: подпись и текст результата (текст сервера — важнее своего). */
const PART_META: Readonly<Record<CheckPart, PartMeta>> = {
  dns: {
    title: "DNS",
    ok: "DNS указывает на сервер",
    fail: (c, expected) => {
      const need = expected.length ? `, нужен ${expected.join(" или ")}` : "";
      return c.dns?.ips?.[0] ? `DNS указывает на ${c.dns.ips.join(", ")}${need}` : `DNS-записи не найдены${need}`;
    },
  },
  proxy: { title: "Прокси", ok: "Хост в NPM отвечает", fail: () => "Хоста в NPM нет" },
  cert: { title: "HTTPS", ok: "Сертификат действует", fail: () => "Нет сертификата" },
};

type Mark = "ok" | "fail" | "none";

function markOf(check: ShopDomainCheck | null | undefined, part: CheckPart): Mark {
  const value = check?.[part];
  if (!value) return "none";
  return value.ok ? "ok" : "fail";
}

const MARK_ICON = { ok: Check, fail: X, none: Minus } as const;
const MARK_CLASS: Readonly<Record<Mark, string>> = {
  ok: "text-[var(--status-success-fg,var(--success))]",
  fail: "text-[var(--status-error-fg,var(--destructive))]",
  none: "text-on-surface-variant",
};

interface MarkProps {
  check: ShopDomainCheck | null | undefined;
  part: CheckPart;
  /** Подпись рядом со значком («DNS ✓») — в карточке; в таблице её даёт шапка. */
  withTitle?: boolean;
  /** IP сервера платформы — «нужен X» в подсказке DNS; нет — без него (кабинет магазина). */
  expectedIps?: readonly string[];
}

export function DomainCheckMark({ check, part, withTitle = false, expectedIps = [] }: MarkProps) {
  const meta = PART_META[part];
  const mark = markOf(check, part);
  const serverError = check?.[part]?.error;
  const Icon = MARK_ICON[mark];
  const days = part === "cert" && mark === "ok" ? certDaysLeft(check) : null;
  const text = mark === "ok" ? meta.ok : mark === "fail" && check ? (serverError || meta.fail(check, expectedIps)) : "Не проверялось";
  const label = days !== null ? `${meta.title}: ${text}, ещё ${days} дн.` : `${meta.title}: ${text}`;
  return (
    <span className="inline-flex items-center gap-1 tabular-nums" title={label} aria-label={label} role="img">
      {withTitle ? <span className="text-on-surface-variant">{meta.title}</span> : null}
      <Icon aria-hidden className={cn("size-4 shrink-0", MARK_CLASS[mark])} />
      {days !== null ? (
        <span className={cn("type-caption", days < CERT_WARN_DAYS ? MARK_CLASS.fail : "text-on-surface-variant")}>{days} дн</span>
      ) : null}
    </span>
  );
}

/** Три проверки в ряд — для карточки адреса и строки списка на телефоне. */
export function DomainCheckMarks({ check, expectedIps }: { check: ShopDomainCheck | null | undefined; expectedIps?: readonly string[] }) {
  return (
    <span className="type-caption flex flex-wrap items-center gap-x-3 gap-y-1">
      {(["dns", "proxy", "cert"] as const).map((part) => (
        <DomainCheckMark key={part} check={check} part={part} withTitle expectedIps={expectedIps} />
      ))}
    </span>
  );
}
